import {
  FILTER_RANGE,
  FILTER_TOGGLE,
  FILTER_TOGGLE_CATEGORY,
  FILTER_TOGGLE_RESET,
  FILTER_UPDATE_PANELS,
  FilterRangeParams,
  FilterToggleAction,
  FilterToggleParams
} from './actions';
import { FilterPage, isFilterPage } from './selector';
import { BasePageModel } from 'models/BasePageModel';
import { ValueFacetFilter } from 'models/ValueFacetFilter';
import { invokeIfExists } from 'utils';
import { FilterDisplayType } from 'models/FilterDisplayType';
import { RangeFacetFilter } from 'models/RangeFacetFilter';
import { FacetGroup } from 'models/FacetGroup';
import { FacetFilter } from 'models/FacetFilter';

type ReducerPageType<S> = BasePageModel | S;
interface Reducer<P, S = ReducerPageType<P>> {
  [key: string]: (state: S, action: FilterToggleAction | FilterPage) => S;
}

const reducer: Reducer<FilterPage> = {
  [FILTER_UPDATE_PANELS]: (state: BasePageModel, action: FilterPage) => {
    return !isFilterPage(state)
      ? state
      : {
          ...state,
          filterPanels: action.filterPanels
        };
  },
  [FILTER_TOGGLE]: (state: BasePageModel, action: FilterToggleAction) => {
    return !isFilterPage(state)
      ? state
      : {
          ...state,
          filterPanels: mapFilterToggle(state.filterPanels, action)
        };
  },
  [FILTER_RANGE]: (state: BasePageModel, action: FilterToggleAction) => {
    return !isFilterPage(state)
      ? state
      : {
          ...state,
          filterPanels: mapFilterToggle(state.filterPanels, action)
        };
  },
  [FILTER_TOGGLE_CATEGORY]: (state: BasePageModel, action: FilterToggleAction) => {
    return !isFilterPage(state)
      ? state
      : {
          ...state,
          filterPanels: setFilterToggleCategory(state, action)
        };
  },
  [FILTER_TOGGLE_RESET]: (state: BasePageModel) => {
    return !isFilterPage(state)
      ? state
      : {
          ...state,
          filterPanels: state.filterPanels.map(mapFilterToggleCategory)
        };
  }
};

const setFilterToggleCategory = (state: FilterPage, action: FilterToggleAction) =>
  state.filterPanels.map(fp => (fp.name !== action.facet ? fp : mapFilterToggleCategory(fp)));

const mapValueFacetFilter = (f: ValueFacetFilter, action: FilterToggleParams) =>
  f.name !== action.facet
    ? f
    : {
        ...f,
        values: f.values.map(v =>
          v.value !== action.filter.value
            ? v
            : {
                ...v,
                selected: !action.filter.selected
              }
        )
      };

type FilterParams =
  | FacetGroup
  | ValueFacetFilter
  | RangeFacetFilter
  | FilterToggleAction
  | FilterToggleParams
  | FilterRangeParams
  | FacetFilter;

type FilterReturnTypes = FacetGroup | ValueFacetFilter | RangeFacetFilter;

const mapFilterToggle = (facets: FacetFilter[], action: FilterToggleAction) =>
  facets.map(f =>
    f.name === action.facet || f.type === FilterDisplayType.Group
      ? invokeIfExists<FilterReturnTypes, FacetFilter, FilterParams>(
          {
            [FilterDisplayType.Group]: (
              facet: FacetGroup,
              action: FilterToggleAction
            ): FacetGroup => ({
              ...facet,
              facets: mapFilterToggle(facet.facets, action)
            }),
            [FilterDisplayType.Standard]: mapValueFacetFilter,
            [FilterDisplayType.NoHeader]: mapValueFacetFilter,
            [FilterDisplayType.SimpleSlider]: (f: RangeFacetFilter, action: FilterRangeParams) =>
              f.name !== action.facet
                ? f
                : {
                    ...f,
                    selectedMin: action.value.min,
                    selectedMax: action.value.max
                  }
          },
          f.type,
          f,
          f,
          action
        )
      : f
  );

const mapCategoryValueFilter = (f: ValueFacetFilter): ValueFacetFilter => ({
  ...f,
  active: false,
  values: f.values.map(v => ({ ...v, selected: false }))
});

const mapFilterToggleCategory = (f: FacetFilter) =>
  invokeIfExists<FilterReturnTypes, FacetFilter, FacetFilter>(
    {
      [FilterDisplayType.Standard]: mapCategoryValueFilter,
      [FilterDisplayType.NoHeader]: mapCategoryValueFilter,
      [FilterDisplayType.SimpleSlider]: (f: RangeFacetFilter): RangeFacetFilter => ({
        ...f,
        active: false,
        selectedMax: f.maxValue,
        selectedMin: f.minValue
      }),
      [FilterDisplayType.Group]: (f: FacetGroup): FacetGroup => ({
        ...f,
        active: false,
        facets: f.facets.map(mapFilterToggleCategory)
      })
    },
    f.type,
    f,
    f
  );

export default reducer;
